import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { IRound } from 'types/Round';

const sortInvitations = (list: IRound[]): IRound[] =>
  [...list].sort((a, b) => {
    const ae = a.invitationExpiresAt ?? Number.MAX_SAFE_INTEGER;
    const be = b.invitationExpiresAt ?? Number.MAX_SAFE_INTEGER;
    if (ae !== be) return ae - be;
    return (a.roomName ?? '').localeCompare(b.roomName ?? '');
  });

interface InvitationState {
  /** Invitations reçues en tant qu'invité (persiste hors du Layout / pendant une partie). */
  inviteeInvitations: IRound[];
}

const initialState: InvitationState = {
  inviteeInvitations: [],
};

export const invitationSlice = createSlice({
  name: 'invitation',
  initialState,
  reducers: {
    setInviteeInvitations: (state, action: PayloadAction<IRound[]>) => {
      state.inviteeInvitations = sortInvitations(action.payload);
    },
    addInviteeInvitation: (state, action: PayloadAction<IRound>) => {
      const round = action.payload;
      if (!round.roomName) return;
      if (
        state.inviteeInvitations.some((inv) => inv.roomName === round.roomName)
      )
        return;
      state.inviteeInvitations = sortInvitations([
        ...state.inviteeInvitations,
        round,
      ]);
    },
    removeInviteeInvitation: (state, action: PayloadAction<string>) => {
      state.inviteeInvitations = state.inviteeInvitations.filter(
        (inv) => inv.roomName !== action.payload,
      );
    },
    pruneExpiredInviteeInvitations: (state, action: PayloadAction<number>) => {
      const now = action.payload;
      state.inviteeInvitations = state.inviteeInvitations.filter(
        (inv) =>
          inv.invitationExpiresAt == null || inv.invitationExpiresAt > now,
      );
    },
    clearInviteeInvitations: (state) => {
      state.inviteeInvitations = [];
    },
  },
});

export const {
  setInviteeInvitations,
  addInviteeInvitation,
  removeInviteeInvitation,
  pruneExpiredInviteeInvitations,
  clearInviteeInvitations,
} = invitationSlice.actions;

export default invitationSlice.reducer;
